/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {ChangeDetectorRef, ComponentFactory, ComponentRef, EventEmitter, Injector, OnChanges, ReflectiveInjector, SimpleChange, SimpleChanges, Type} from '@angular/core';

import * as angular from './angular1';
import {PropertyBinding} from './component_info';
import {$SCOPE} from './constants';
import {getAttributesAsArray, getComponentName, hookupNgModel, strictEquals} from './util';

const /** @type {?} */ INITIAL_VALUE = {
  __UNINITIALIZED__: true
};
export class DowngradeComponentAdapter {
private inputChangeCount: number = 0;
private inputChanges: SimpleChanges|null = null;
private componentScope: angular.IScope;
private componentRef: ComponentRef<any>|null = null;
private component: any = null;
private changeDetector: ChangeDetectorRef|null = null;
/**
 * @param {?} element
 * @param {?} attrs
 * @param {?} scope
 * @param {?} ngModel
 * @param {?} parentInjector
 * @param {?} $injector
 * @param {?} $compile
 * @param {?} $parse
 * @param {?} componentFactory
 */
constructor(
private element: angular.IAugmentedJQuery,
private attrs: angular.IAttributes,
private scope: angular.IScope,
private ngModel: angular.INgModelController,
private parentInjector: Injector,
private $injector: angular.IInjectorService,
private $compile: angular.ICompileService,
private $parse: angular.IParseService,
private componentFactory: ComponentFactory<any>) {
    this.componentScope = scope.$new();
  }
/**
 * @return {?}
 */
compileContents(): Node[][] {
    const /** @type {?} */ compiledProjectableNodes: Node[][] = [];
    const /** @type {?} */ projectableNodes: Node[][] = this.groupProjectableNodes();
    const /** @type {?} */ linkFns = projectableNodes.map(nodes => this.$compile(nodes)); /** @type {?} */((

    this.element.empty))();

    linkFns.forEach(linkFn => {
      linkFn(this.scope, (clone: Node[]) => {
        compiledProjectableNodes.push(clone); /** @type {?} */((
        this.element.append))(clone);
      });
    });

    return compiledProjectableNodes;
  }
/**
 * @param {?} projectableNodes
 * @return {?}
 */
createComponent(projectableNodes: Node[][]) {
    const /** @type {?} */ childInjector = ReflectiveInjector.resolveAndCreate(
        [{provide: $SCOPE, useValue: this.componentScope}], this.parentInjector);

    this.componentRef =
        this.componentFactory.create(childInjector, projectableNodes, this.element[0]);
    this.changeDetector = this.componentRef.changeDetectorRef;
    this.component = this.componentRef.instance;

    hookupNgModel(this.ngModel, this.component);
  }
/**
 * @return {?}
 */
setupInputs(): void {
    const /** @type {?} */ attrs = this.attrs;
    const /** @type {?} */ inputs = this.componentFactory.inputs || [];
    for (let /** @type {?} */ i = 0; i < inputs.length; i++) {
      const /** @type {?} */ input = new PropertyBinding(inputs[i].propName, inputs[i].templateName);
      let /** @type {?} */ expr: string|null = null;

      if (attrs.hasOwnProperty(input.attr)) {
        const /** @type {?} */ observeFn = (prop => {
          let /** @type {?} */ prevValue = INITIAL_VALUE;
          return (currValue: any) => {
            // Initially, both `$observe()` and `$watch()` will call this function.
            if (!strictEquals(prevValue, currValue)) {
              if (prevValue === INITIAL_VALUE) {
                prevValue = currValue;
              }

              this.updateInput(prop, prevValue, currValue);
              prevValue = currValue;
            }
          };
        })(input.prop);
        attrs.$observe(input.attr, observeFn);

        // Use `$watch()` (in addition to `$observe()`) in order to initialize the input  in time
        // for `ngOnChanges()`. This is necessary if we are already in a `$digest`, which means that
        // `ngOnChanges()` (which is called by a watcher) will run before the `$observe()` callback.
        let /** @type {?} */ unwatch: Function|null = this.componentScope.$watch(() => { /** @type {?} */((
          unwatch))();
          unwatch = null;
          observeFn(attrs[input.attr]);
        });

      } else if (attrs.hasOwnProperty(input.bindAttr)) {
        expr = attrs[input.bindAttr];
      } else if (attrs.hasOwnProperty(input.bracketAttr)) {
        expr = attrs[input.bracketAttr];
      } else if (attrs.hasOwnProperty(input.bindonAttr)) {
        expr = attrs[input.bindonAttr];
      } else if (attrs.hasOwnProperty(input.bracketParenAttr)) {
        expr = attrs[input.bracketParenAttr];
      }
      if (expr != null) {
        const /** @type {?} */ watchFn =
            (prop => (currValue: any, prevValue: any) =>
                 this.updateInput(prop, prevValue, currValue))(input.prop);
        this.componentScope.$watch(expr, watchFn);
      }
    }

    const /** @type {?} */ prototype = this.componentFactory.componentType.prototype;
    if (prototype && ( /** @type {?} */((<OnChanges>prototype))).ngOnChanges) {
      // Detect: OnChanges interface
      this.inputChanges = {};
      this.componentScope.$watch(() => this.inputChangeCount, () => {
        const /** @type {?} */ inputChanges = this.inputChanges;
        this.inputChanges = {};
        ( /** @type {?} */((<OnChanges>this.component))).ngOnChanges( /** @type {?} */((inputChanges)));
      });
    }
    this.componentScope.$watch(() => this.changeDetector && this.changeDetector.detectChanges());
  }
/**
 * @return {?}
 */
setupOutputs() {
    const /** @type {?} */ attrs = this.attrs;
    const /** @type {?} */ outputs = this.componentFactory.outputs || [];
    for (let /** @type {?} */ j = 0; j < outputs.length; j++) {
      const /** @type {?} */ output = new PropertyBinding(outputs[j].propName, outputs[j].templateName);
      let /** @type {?} */ expr: string|null = null;
      let /** @type {?} */ assignExpr = false;

      const /** @type {?} */ bindonAttr = output.bindonAttr.substring(0, output.bindonAttr.length - 6);
      const /** @type {?} */ bracketParenAttr =
          `[(${output.bracketParenAttr.substring(2, output.bracketParenAttr.length - 8)})]`;

      if (attrs.hasOwnProperty(output.onAttr)) {
        expr = attrs[output.onAttr];
      } else if (attrs.hasOwnProperty(output.parenAttr)) {
        expr = attrs[output.parenAttr];
      } else if (attrs.hasOwnProperty(bindonAttr)) {
        expr = attrs[bindonAttr];
        assignExpr = true;
      } else if (attrs.hasOwnProperty(bracketParenAttr)) {
        expr = attrs[bracketParenAttr];
        assignExpr = true;
      }

      if (expr != null && assignExpr != null) {
        const /** @type {?} */ getter = this.$parse(expr);
        const /** @type {?} */ setter = getter.assign;
        if (assignExpr && !setter) {
          throw new Error(`Expression '${expr}' is not assignable!`);
        }
        const /** @type {?} */ emitter = /** @type {?} */(( this.component[output.prop] as EventEmitter<any>));
        if (emitter) {
          emitter.subscribe({
            next: assignExpr ? (v: any) => /** @type {?} */(( setter))(this.scope, v) :
                               (v: any) => getter(this.scope, {'$event': v})
          });
        } else {
          throw new Error(
              `Missing emitter '${output.prop}' on component '${getComponentName(this.componentFactory.componentType)}'!`);
        }
      }
    }
  }
/**
 * @return {?}
 */
registerCleanup() { /** @type {?} */((
    this.element.bind))('$destroy', () => {
      this.componentScope.$destroy(); /** @type {?} */((
      this.componentRef)).destroy();
    });
  }
/**
 * @return {?}
 */
getInjector(): Injector { return /** @type {?} */(( this.componentRef)) && /** @type {?} */(( this.componentRef)).injector; }
/**
 * @param {?} prop
 * @param {?} prevValue
 * @param {?} currValue
 * @return {?}
 */
private updateInput(prop: string, prevValue: any, currValue: any) {
    if (this.inputChanges) {
      this.inputChangeCount++;
      this.inputChanges[prop] = new SimpleChange(prevValue, currValue, prevValue === currValue);
    }

    this.component[prop] = currValue;
  }
/**
 * @return {?}
 */
groupProjectableNodes() {
    let /** @type {?} */ ngContentSelectors = this.componentFactory.ngContentSelectors;
    return groupNodesBySelector(ngContentSelectors, /** @type {?} */(( this.element.contents))());
  }
}

function DowngradeComponentAdapter_tsickle_Closure_declarations() {
/** @type {?} */
DowngradeComponentAdapter.prototype.inputChangeCount;
/** @type {?} */
DowngradeComponentAdapter.prototype.inputChanges;
/** @type {?} */
DowngradeComponentAdapter.prototype.componentScope;
/** @type {?} */
DowngradeComponentAdapter.prototype.componentRef;
/** @type {?} */
DowngradeComponentAdapter.prototype.component;
/** @type {?} */
DowngradeComponentAdapter.prototype.changeDetector;
/** @type {?} */
DowngradeComponentAdapter.prototype.element;
/** @type {?} */
DowngradeComponentAdapter.prototype.attrs;
/** @type {?} */
DowngradeComponentAdapter.prototype.scope;
/** @type {?} */
DowngradeComponentAdapter.prototype.ngModel;
/** @type {?} */
DowngradeComponentAdapter.prototype.parentInjector;
/** @type {?} */
DowngradeComponentAdapter.prototype.$injector;
/** @type {?} */
DowngradeComponentAdapter.prototype.$compile;
/** @type {?} */
DowngradeComponentAdapter.prototype.$parse;
/** @type {?} */
DowngradeComponentAdapter.prototype.componentFactory;
}

/**
 * Group a set of DOM nodes into `ngContent` groups, based on the given content selectors.
 * @param {?} ngContentSelectors
 * @param {?} nodes
 * @return {?}
 */
export function groupNodesBySelector(ngContentSelectors: string[], nodes: Node[]): Node[][] {
  const /** @type {?} */ projectableNodes: Node[][] = [];
  let /** @type {?} */ wildcardNgContentIndex: number;

  for (let /** @type {?} */ i = 0, /** @type {?} */ ii = ngContentSelectors.length; i < ii; ++i) {
    projectableNodes[i] = [];
  }

  for (let /** @type {?} */ j = 0, /** @type {?} */ jj = nodes.length; j < jj; ++j) {
    const /** @type {?} */ node = nodes[j];
    const /** @type {?} */ ngContentIndex = findMatchingNgContentIndex(node, ngContentSelectors);
    if (ngContentIndex != null) {
      projectableNodes[ngContentIndex].push(node);
    }
  }

  return projectableNodes;
}
/**
 * @param {?} element
 * @param {?} ngContentSelectors
 * @return {?}
 */
function findMatchingNgContentIndex(element: any, ngContentSelectors: string[]): number|null {
  const /** @type {?} */ ngContentIndices: number[] = [];
  let /** @type {?} */ wildcardNgContentIndex: number = -1;
  for (let /** @type {?} */ i = 0; i < ngContentSelectors.length; i++) {
    const /** @type {?} */ selector = ngContentSelectors[i];
    if (selector === '*') {
      wildcardNgContentIndex = i;
    } else {
      if (matchesSelector(element, selector)) {
        ngContentIndices.push(i);
      }
    }
  }
  ngContentIndices.sort();

  if (wildcardNgContentIndex !== -1) {
    ngContentIndices.push(wildcardNgContentIndex);
  }
  return ngContentIndices.length ? ngContentIndices[0] : null;
}

let /** @type {?} */ _matches: (this: any, selector: string) => boolean;
/**
 * @param {?} el
 * @param {?} selector
 * @return {?}
 */
function matchesSelector(el: any, selector: string): boolean {
  if (!_matches) {
    const /** @type {?} */ elProto = /** @type {?} */(( <any>Element.prototype));
    _matches = elProto.matches || elProto.matchesSelector || elProto.mozMatchesSelector ||
        elProto.msMatchesSelector || elProto.oMatchesSelector || elProto.webkitMatchesSelector;
  }
  return el.nodeType === Node.ELEMENT_NODE ? _matches.call(el, selector) : false;
}
